'use client';

import { useEffect, useMemo, useState } from 'react';
import type { AssetGraphResponseDto } from '@terra/shared-contracts-external-network';

export type ExternalNetworkClusterControl = {
  id: string;
  label: string;
};

type AssetGraphNode = AssetGraphResponseDto['nodes'][number];

const mergeClusterControls = ({
  currentControls,
  clusterNodes,
}: {
  currentControls: ExternalNetworkClusterControl[];
  clusterNodes: AssetGraphNode[];
}): ExternalNetworkClusterControl[] => {
  const currentById = new Map(
    currentControls.map((control) => [control.id, control]),
  );
  const latestById = new Map(
    clusterNodes.map((node) => [
      node.id,
      {
        id: node.id,
        label: node.label,
      } satisfies ExternalNetworkClusterControl,
    ]),
  );
  const currentIds = currentControls.map((control) => control.id);
  const orderedIds = [
    ...currentIds,
    ...clusterNodes
      .map((node) => node.id)
      .filter((id) => !currentIds.includes(id)),
  ];

  return orderedIds
    .map((id) => latestById.get(id) ?? currentById.get(id))
    .filter((control): control is ExternalNetworkClusterControl =>
      Boolean(control),
    );
};

export function useExternalNetworkClusterControls({
  graph,
  expandedClusterIds,
  isNeighborhoodActive,
}: {
  graph: AssetGraphResponseDto | null | undefined;
  expandedClusterIds: string[];
  isNeighborhoodActive: boolean;
}): ExternalNetworkClusterControl[] {
  const [clusterControls, setClusterControls] = useState<
    ExternalNetworkClusterControl[]
  >([]);
  const clusterNodes = useMemo(
    () =>
      graph?.nodes.filter(
        (node) => node.nodeType === 'cluster' && Boolean(node.cluster),
      ) ?? [],
    [graph],
  );

  useEffect(() => {
    if (isNeighborhoodActive) {
      setClusterControls([]);

      return;
    }

    if (clusterNodes.length === 0) {
      if (expandedClusterIds.length === 0) {
        setClusterControls([]);
      }

      return;
    }

    setClusterControls((currentControls) =>
      mergeClusterControls({ currentControls, clusterNodes }),
    );
  }, [clusterNodes, expandedClusterIds, isNeighborhoodActive]);

  return clusterControls;
}
